import { AutomationReportViewModel, IEmailAutomationStep } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { computed, makeObservable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { toast } from 'sonner';
import { ConditionalAccess } from '../../../../../admin/components/ConditionalAccess';
import { IAdminUserSessionComponentProps } from '../../../../../models/AdminModels';
import {
	FullScreenModalViewModelKey,
	IFullscreenModalComponentProps,
	UserSessionViewModelKey,
} from '../../../../../models/AppState';
import { copyToClipboard } from '../../../../../models/UiUtils';
import { AdminPermission } from '../../../../../viewmodels/AdminViewModels';
import { Contacts } from '../../../../containers/reporting/Campaigns/Contacts';
import { baseStyleSheet } from '../../../../styles/styles';
import { Checkbox } from '../../../Checkbox';
import { ClipboardIcon } from '../../../svgs/icons/ClipboardIcon';
import { styleSheet as groupStyleSheet } from '../GroupEmailTableRowExpandable/styles';
import { GroupEmailTableRowStatusDisplay } from '../GroupEmailTableRowStatus/GroupEmailTableRowStatusDisplay';
import { getCampaignStatusValues } from '../GroupEmailTableRowStatus/utils';
import { GroupEmailTags } from '../GroupEmailTags';
import { styleSheet } from './styles';

export interface IGroupEmailTableRowCallbacks {
	onCancelAutomationClick?(e: React.MouseEvent<HTMLElement>): void;
	onCancelAutomationStepClick?(e: React.MouseEvent<HTMLElement>): void;
	onCheckChanged?: (checked: boolean) => void;
	onClick?(e: React.MouseEvent<HTMLElement>): void;
	onManageAutomationClick?(e: React.MouseEvent<HTMLElement>): void;
	onSubjectClicked?(e: React.MouseEvent<HTMLElement>): void;
	isLastRow?: boolean;
}

interface IProps
	extends IGroupEmailTableRowCallbacks,
		IAdminUserSessionComponentProps,
		RouteComponentProps<any>,
		IFullscreenModalComponentProps {
	className?: string;
	isGroupItem?: boolean;
	preventAutomationFunctionality?: boolean;
	styles?: StyleDeclarationValue[];
	viewModel: AutomationReportViewModel;
}

class _GroupEmailTableAutomationRow extends React.PureComponent<IProps> {
	state: { checked: boolean } = {
		checked: false,
	};
	handleCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.stopPropagation();
		const checked = e.target.checked;
		this.setState(
			{
				checked,
			},
			() => {
				this.props.onCheckChanged(checked);
			}
		);
	};

	constructor(props: IProps) {
		super(props);

		makeObservable<_GroupEmailTableAutomationRow, 'elementDataProps'>(this, {
			elementDataProps: computed,
		});
	}

	public render() {
		const { className, onClick, styles, viewModel, onCheckChanged, isLastRow } = this.props;
		return (
			<div
				className={`group-email-table-row ${css(isLastRow ? styleSheet.rowLast : styleSheet.row, ...(styles || []))} ${className || ''}`}
				onClick={onClick}
				{...this.elementDataProps}
			>
				{onCheckChanged ? (
					<div className={css(styleSheet.checkboxCol)}>
						<Checkbox
							id={`campaign-checkbox-${viewModel.id}`}
							checked={this.state.checked}
							onChange={this.handleCheckChange}
							onClick={e => {
								e.stopPropagation();
							}}
						/>
					</div>
				) : null}
				{this.renderSubject()}
				{this.renderSentToCol()}
				{this.renderAutomationActionsColumn()}
			</div>
		);
	}

	private get elementDataProps() {
		if (!import.meta.env.PROD && this.props.viewModel) {
			return {
				['data-id']: this.props.viewModel.id || '',
			};
		}
		return {};
	}

	private onRenderTagsFallback = () => {
		const { viewModel } = this.props;
		if (viewModel.sampleContacts?.length) {
			return this.renderContacts();
		}
		return null;
	};

	private renderAutomationActionsColumn = () => {
		const {
			onCancelAutomationClick,
			onCancelAutomationStepClick,
			onManageAutomationClick,
			preventAutomationFunctionality,
		} = this.props;

		if (!preventAutomationFunctionality) {
			return (
				<div className={css(styleSheet.automationCol)}>
					<div className={css(styleSheet.automationColLinkWrapper)}>
						<button className={`${css(baseStyleSheet.brandLink, styleSheet.button)}`} onClick={onCancelAutomationClick}>
							<span className={css(baseStyleSheet.whiteSpaceNoWrap)}>Cancel Automation</span>
						</button>
						<button
							className={`${css(baseStyleSheet.brandLink, styleSheet.button)}`}
							onClick={onCancelAutomationStepClick}
						>
							<span className={css(baseStyleSheet.whiteSpaceNoWrap)}>Cancel Step</span>
						</button>
					</div>
					<div className={css(styleSheet.automationColButtonWrapper)}>
						<button
							className={`${css(baseStyleSheet.ctaButton, styleSheet.button, styleSheet.manageAutomationCTA)}`}
							onClick={onManageAutomationClick}
						>
							<span>Manage</span>
						</button>
					</div>
				</div>
			);
		}

		return null;
	};

	private renderContacts() {
		const { viewModel } = this.props;

		return (
			<div className={css(styleSheet.contacts)}>
				<Contacts maxContacts={3} totalContacts={viewModel.sampleContacts.length} contacts={viewModel.sampleContacts} />
			</div>
		);
	}

	private renderSentToCol() {
		const { viewModel } = this.props;
		return (
			<GroupEmailTags onRenderFallback={this.onRenderTagsFallback} styles={[styleSheet.tags]} viewModel={viewModel} />
		);
	}

	private renderStatus = () => {
		const { viewModel } = this.props;
		if (!viewModel.status) {
			return null;
		}
		const values = getCampaignStatusValues(viewModel);
		return (
			<GroupEmailTableRowStatusDisplay
				color={values.colorStyle}
				date={new Date(values.dateString)}
				datePrefix={values.datePrefix}
				status={values.statusText}
				userRef={viewModel.creator}
				styles={[styleSheet.status]}
			/>
		);
	};

	private renderSubject = () => {
		const { isGroupItem, onSubjectClicked, preventAutomationFunctionality, viewModel } = this.props;
		let subject: string = null;
		if (
			viewModel.automationStep._type === 'EmailAutomationStep' ||
			viewModel.automationStep._type === 'HtmlNewsletterAutomationStep'
		) {
			const emailStep = viewModel.automationStep as IEmailAutomationStep;
			subject = emailStep.options.subject;
		}
		return (
			<div className={css(styleSheet.subjectCell, isGroupItem ? groupStyleSheet.groupItemSubject : null)}>
				<div className={css(styleSheet.subject)} onClick={onSubjectClicked}>
					<div
						className={css(
							baseStyleSheet.brandLink,
							baseStyleSheet.truncateText,
							preventAutomationFunctionality && styleSheet.unClickableSubject
						)}
					>
						{subject}
					</div>
					<ConditionalAccess permission={AdminPermission.ReadAccount}>
						<button
							onClick={e => {
								e.stopPropagation();
								copyToClipboard(this.props.viewModel?.id ?? '');
								toast.success('ID copied to clipboard');
							}}
							className={css(styleSheet.copyIdIcon)}
						>
							<ClipboardIcon className={css(styleSheet.linkIcon)} />
						</button>
					</ConditionalAccess>
				</div>
				<span className={css(styleSheet.automationSteps)}>
					{`Step ${viewModel.stepIndex + 1}/${viewModel.stepCount} of Automation: `}
					<span className={css(styleSheet.automationName)}>{viewModel.automationName}</span>
				</span>
				{this.renderStatus()}
			</div>
		);
	};
}

const GroupEmailTableAutomationRowAsObserver = observer(_GroupEmailTableAutomationRow);
const GroupEmailTableAutomationRowWithRouter = withRouter(GroupEmailTableAutomationRowAsObserver);
export const GroupEmailTableAutomationRow = inject(
	UserSessionViewModelKey,
	FullScreenModalViewModelKey
)(GroupEmailTableAutomationRowWithRouter);
